import { useRoleCheck } from '@/hooks/useRoleCheck';
import { Permission } from '@/types/rolesTypes';
import { buildPermissionMap, can, hasAnyAccess, PermissionMap } from '@/utils/managePermission';
import { createContext, useContext, useMemo, ReactNode } from 'react';

type AccessType = 'read' | 'create' | 'update' | 'delete' | 'download' | 'detailedView';

type PrivilegeItem = {
  updatedAccess?: Partial<Record<AccessType, boolean>>;
  privilege?: {
    permissions?: string;
  };
};

interface PermissionContextValue {
  permissionMap: PermissionMap;
  isOwner: boolean;
  hasAnyAccess: (key: string) => boolean;
  can: (key: string, action: string) => boolean;
  hasModuleAccess: (moduleName: string, accessType: AccessType) => boolean;
  user: Permission;
  isLoading: boolean;
  isDownlineUser: boolean;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

export function PermissionProvider({
  user,
  children,
  isLoading,
}: {
  user: Permission;
  children: ReactNode;
  isLoading: boolean;
}) {
  const { isOwner, isDownlineUser } = useRoleCheck();

  const permissionMap = useMemo(() => buildPermissionMap(user), [user]);

  const allowedActions: PrivilegeItem[] = user?.role?.rolePrivileges ?? [];

  const hasModuleAccess = (moduleName: string, accessType: AccessType): boolean => {
    if (isOwner || isDownlineUser) return true;

    const moduleData = allowedActions.find((item) => item?.privilege?.permissions === moduleName);

    return moduleData?.updatedAccess?.[accessType] === true;
  };

  const value = useMemo(
    () => ({
      permissionMap,
      isOwner,
      isDownlineUser,
      user,
      hasAnyAccess: (key: string) => isOwner || isDownlineUser || hasAnyAccess(permissionMap, key),
      can: (key: string, action: string) => isOwner || isDownlineUser || can(permissionMap, key, action),
      hasModuleAccess,
      isLoading,
    }),
    [permissionMap, user, isOwner, allowedActions, isDownlineUser]
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions() {
  const ctx = useContext(PermissionContext);

  if (!ctx) {
    throw new Error('usePermissions must be used inside PermissionProvider');
  }

  return ctx;
}

// import { useRoleCheck } from '@/hooks/useRoleCheck';
// import { Permission } from '@/types/rolesTypes';
// import { buildPermissionMap, can, hasAnyAccess, PermissionMap } from '@/utils/managePermission';
// import { createContext, useContext, useMemo, ReactNode } from 'react';

// type AccessType = 'read' | 'create' | 'update' | 'delete' | 'download' | 'detailedView';

// type PrivilegeItem = {
//   updatedAccess?: Partial<Record<AccessType, boolean>>;
//   privilege?: {
//     permissions?: string;
//   };
// };
// interface PermissionContextValue {
//   permissionMap: PermissionMap;
//   isOwner: boolean;
//   hasAnyAccess: (key: string) => boolean;
//   can: (key: string, action: string) => boolean;
//   hasModuleAccess: (moduleName: string, accessType: AccessType) => boolean;
//   user: Permission | undefined;
// }

// // Step 1: create the context
// const PermissionContext = createContext<PermissionContextValue | null>(null);
// // Step 2: provider component
// export function PermissionProvider({ user, children }: { user: Permission; children: ReactNode }) {
//   const { isOwner } = useRoleCheck();
//   const permissionMap = useMemo(() => buildPermissionMap(user), [user]);

//   const allowedActions: PrivilegeItem[] = user?.role?.rolePrivileges ?? [];

//   const hasModuleAccess = (moduleName: string, accessType: AccessType): boolean => {
//     if (isOwner) return true;

//     const moduleData = allowedActions.find(
//       (item: PrivilegeItem) => item?.privilege?.permissions === moduleName
//     );

//     return moduleData?.updatedAccess?.[accessType] === true;
//   };

//   const value = useMemo(
//     () => ({
//       permissionMap,
//       isOwner,
//       user,
//       hasAnyAccess: (key: string) => isOwner || hasAnyAccess(permissionMap, key),
//       can: (key: string, action: string) => isOwner || can(permissionMap, key, action),
//       hasModuleAccess,
//     }),
//     [permissionMap, user, isOwner, allowedActions]
//   );

//   return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
// }

// // Step 3: hook to consume it
// export function usePermissions() {
//   const ctx = useContext(PermissionContext);
//   if (!ctx) throw new Error('usePermissions must be used inside PermissionProvider');
//   return ctx;
// }
